import { Stack, Typography } from '@sureco/design-system'
import { Skeleton } from '@surecompanies/core_components'
import ContentSection from 'layouts/ContentSection/ContentSection'
import React from 'react'
import { CompanyContact } from 'types/company'
import { PAYROLL_CYCLE_OPTIONS } from '../constants'
import { DemoCustomerConfig } from '../types'

interface DemoCustomerSummaryProps {
  config: DemoCustomerConfig
  /**
   * Live contacts from GET /customers/:id — same data shown on the Profile
   * tab. When provided, the Sales Contact section renders from this array
   * (SURECO-type contacts).
   */
  contacts?: CompanyContact[]
  /** Shows a skeleton placeholder in the Enrollment Period section while dates are loading. */
  enrollmentLoading?: boolean
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function LabelValue({
  label,
  value,
}: {
  label: string
  value?: React.ReactNode
}) {
  return (
    <Stack space={2}>
      <Typography size={12} color="#8c8c8c">
        {label}
      </Typography>
      <Typography size={14}>
        {value ?? <span style={{ color: '#bfbfbf' }}>—</span>}
      </Typography>
    </Stack>
  )
}

function payrollCycleLabel(value: string): string {
  return PAYROLL_CYCLE_OPTIONS.find((o) => o.value === value)?.label ?? value
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—'
  return dateStr
}

// ─── component ───────────────────────────────────────────────────────────────

export default function DemoCustomerSummary({
  config,
  contacts,
  enrollmentLoading = false,
}: DemoCustomerSummaryProps) {
  if (!config) return null

  // Prefer live contacts from the API (SURECO type = SureCo / sales contacts).
  const surecoContacts = contacts?.filter((c) => c.type === 'SURECO') ?? []
  const hasSurecoContacts = surecoContacts.length > 0

  // Fallback to the salesContact field on the config when live contacts
  // haven't loaded or the customer has no SURECO contact on record.
  const mockContact = config.salesContact
  const hasMockContact =
    !hasSurecoContacts &&
    mockContact &&
    Object.values(mockContact).some((v) => v !== undefined && v !== '')

  return (
    <Stack space={16}>
      {/* ── Customer ────────────────────────────────────────────────── */}
      <ContentSection title="Customer">
        <Stack space={16}>
          {config.logoUrl && (
            <img
              src={config.logoUrl}
              alt="Customer logo"
              style={{ maxHeight: 48, maxWidth: 180, objectFit: 'contain' }}
            />
          )}
          <Stack direction="row" space={32} style={{ flexWrap: 'wrap' }}>
            <LabelValue label="Company Name" value={config.companyName} />
            <LabelValue
              label="Payroll Cycle"
              value={payrollCycleLabel(config.payrollCycle)}
            />
          </Stack>
        </Stack>
      </ContentSection>

      {/* ── Enrollment Period ────────────────────────────────────────── */}
      <ContentSection title="Enrollment Period">
        {enrollmentLoading ? (
          <Skeleton
            active
            paragraph={{ rows: 1, width: ['60%'] }}
            title={false}
          />
        ) : (
          <Stack direction="row" space={32} style={{ flexWrap: 'wrap' }}>
            <LabelValue
              label="Benefit Effective Date"
              value={formatDate(config.benefitEffectiveDate)}
            />
            <LabelValue
              label="Enrollment Start"
              value={formatDate(config.enrollmentStartDate)}
            />
            <LabelValue
              label="Enrollment End"
              value={formatDate(config.enrollmentEndDate)}
            />
          </Stack>
        )}
      </ContentSection>

      {/* ── Sales Contact — live from GET /customers/:id contacts ─────── */}
      {hasSurecoContacts && (
        <ContentSection title="Sales Contact">
          <Stack space={20}>
            {surecoContacts.map((c) => (
              <Stack
                key={`${c.type}_${c.fullName}`}
                direction="row"
                space={32}
                style={{ flexWrap: 'wrap' }}
              >
                {c.fullName && (
                  <LabelValue label="Contact Name" value={c.fullName} />
                )}
                {c.title && <LabelValue label="Title" value={c.title} />}
                {c.phone?.number && (
                  <LabelValue label="Phone" value={c.phone.number} />
                )}
                {c.email && (
                  <LabelValue
                    label="Email"
                    value={<a href={`mailto:${c.email}`}>{c.email}</a>}
                  />
                )}
              </Stack>
            ))}
          </Stack>
        </ContentSection>
      )}

      {/* ── Sales Contact fallback — config.salesContact field ────────── */}
      {hasMockContact && (
        <ContentSection title="Sales Contact">
          <Stack direction="row" space={32} style={{ flexWrap: 'wrap' }}>
            {mockContact?.fullName && (
              <LabelValue label="Contact Name" value={mockContact.fullName} />
            )}
            {mockContact?.title && (
              <LabelValue label="Title" value={mockContact.title} />
            )}
            {mockContact?.phone && (
              <LabelValue label="Phone" value={mockContact.phone} />
            )}
            {mockContact?.email && (
              <LabelValue
                label="Email"
                value={
                  <a href={`mailto:${mockContact.email}`}>
                    {mockContact.email}
                  </a>
                }
              />
            )}
          </Stack>
        </ContentSection>
      )}
    </Stack>
  )
}
